/**
 * Horizontal swipe carousel — client revision (Sep 2026), shared by the
 * gallery and the reviews block.
 *
 * Pure CSS scroll-snap, no library: the scroller is a plain overflow-x
 * container with `snap-x snap-mandatory`, so touch swiping is native and
 * each child (which brings its own `snap-start shrink-0 w-[85%] …` sizing,
 * where 85% leaves the ~15% peek that reads as "swipe me" on mobile)
 * settles cleanly. The scrollbar is hidden visually but the container stays
 * keyboard-accessible: it is a focusable region, and a focused scroll
 * container pans with the arrow keys natively.
 *
 * Arrow buttons are desktop-only (hidden below lg) — on touch the swipe IS
 * the affordance. They scroll by ~80% of the viewport so a partially
 * visible card becomes fully visible rather than skipped past.
 */

import { useRef } from 'react';
import { useCopy } from '../../content';
import { useShopConfig } from '../../hooks/useShopConfig';
import { Icon } from '../icons';
import type { ReactNode } from 'react';

export default function Carousel({ label, children }: { label: string; children: ReactNode }) {
  const c = useCopy().home.carousel;
  const { t } = useShopConfig();
  const scroller = useRef<HTMLDivElement>(null);

  const nudge = (dir: 1 | -1) => {
    const el = scroller.current;
    if (el) el.scrollBy({ left: dir * el.clientWidth * 0.8, behavior: 'smooth' });
  };

  const arrow =
    'flex h-11 w-11 items-center justify-center rounded-full border border-edge-1 bg-white text-navy transition-colors hover:border-navy hover:bg-navy hover:text-white';

  return (
    <div>
      <div
        ref={scroller}
        role="region"
        aria-label={label}
        tabIndex={0}
        className="-mx-6 flex snap-x snap-mandatory gap-5 overflow-x-auto px-6 pb-3 [-ms-overflow-style:none] [scrollbar-width:none] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-gold [&::-webkit-scrollbar]:hidden md:mx-0 md:px-0"
      >
        {children}
      </div>
      <div className="mt-6 hidden justify-end gap-3 lg:flex">
        <button type="button" aria-label={t(c.prev)} onClick={() => nudge(-1)} className={arrow}>
          <Icon name="chevronLeft" size={20} />
        </button>
        <button type="button" aria-label={t(c.next)} onClick={() => nudge(1)} className={arrow}>
          <Icon name="chevronRight" size={20} />
        </button>
      </div>
    </div>
  );
}
